import { React, useState, useEffect, useCallback } from 'react';
import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { storage } from '@goldblack/client-storage';
import { AdminMlStatus, AdminMlScaleUp, AdminMlScaleDown, AdminMlAwsLaunchTemplates } from '../../../api/admin-ml';
import { AdminMenuBar, AutoRefresh } from '../../';

// k6 load-test instances are launched from Infrastructure > AWS with a launch template named `k6-<region>`;
// the template name becomes the instance's `template` tag.
const K6_PREFIX = 'k6';
const K6_METRICS_PORT = 8080; // metrics agent on the k6 box (see admin-ml-status.js)
const CHIP_SX = { height: 18, fontSize: 10 };
const HEAD = { fontWeight: 600, color: 'text.secondary' };

const notify = (text) => storage.transience.setState('internal.error', text);

function AccountMl() {
    const [status, setStatus] = useState({ loading: false, data: {} });
    const [busy, setBusy] = useState(false);
    const refresh = useCallback(() => AdminMlStatus(setStatus), []);
    const isLocal = window.location.hostname === 'localhost';

    useEffect(() => {
        refresh();
        AdminMlAwsLaunchTemplates(undefined, true); // k6-* templates of every selected region
    }, [refresh]);

    const instances = Object.values(status.data ?? {})
        .flatMap((e) => e.instances)
        .filter((i) => i.template_id?.toLowerCase().startsWith(K6_PREFIX))
        .sort((a, b) => (a.region ?? '').localeCompare(b.region ?? ''));
    const live = instances.filter((i) => i.status !== 'terminating');

    // Runs one request per item, then reports the first error (if any) and refreshes.
    const runAll = (items, call, done) => {
        if (!items.length) return notify(done.none);
        setBusy(true);
        let left = items.length;
        let error = null;
        items.forEach((item) =>
            call(item, (err) => {
                error ??= err;
                if (--left) return;
                setBusy(false);
                if (error) notify(error);
                refresh();
            }),
        );
    };

    // New: one instance per k6 template whose region has none running yet.
    const launchAll = () => {
        const templates = (storage.transience.getValue('infrastructure')?.awsLaunchTemplates ?? []).filter((t) =>
            t.name?.toLowerCase().startsWith(K6_PREFIX),
        );
        const missing = templates.filter(
            (t) => !live.some((i) => i.template_id?.toLowerCase() === t.name.toLowerCase() && i.region === t.region),
        );
        runAll(missing, (t, cb) => AdminMlScaleUp(t.id, t.name, t.region, cb), {
            none: templates.length ? 'All k6 instances are already running.' : 'No k6-* launch templates found.',
        });
    };

    const terminateAll = () =>
        runAll(live, (i, cb) => AdminMlScaleDown(i.id, i.template_id, i.region, cb), {
            none: 'No running k6 instances.',
        });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: 1 }}>
                <AutoRefresh onRefresh={refresh} defaultMs={5_000} defaultOn />
            </Box>
            <AdminMenuBar
                apiItems={[
                    { label: 'New', onClick: launchAll, disabled: isLocal || busy },
                    { label: 'Refresh', onClick: refresh },
                    { label: 'Delete', onClick: terminateAll, disabled: isLocal || busy },
                ]}
            />

            {instances.length === 0 ? (
                <Typography variant='body2' color='text.disabled' sx={{ p: 2 }}>
                    No k6 instances. Launch one from Infrastructure &gt; AWS with a k6-* template.
                </Typography>
            ) : (
                <Table
                    size='small'
                    sx={{
                        width: '100%',
                        '& .MuiTableCell-root': { border: '1px solid', borderColor: 'divider', py: 0.5, px: 1 },
                        '& td': { whiteSpace: 'nowrap' },
                    }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={HEAD}>#</TableCell>
                            <TableCell sx={HEAD}>Address</TableCell>
                            <TableCell sx={HEAD}>Region</TableCell>
                            <TableCell sx={HEAD}>Status</TableCell>
                            <TableCell sx={HEAD}>CPU</TableCell>
                            <TableCell sx={HEAD}>Mem</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instances.map((i, idx) => (
                            <TableRow key={i.id} hover>
                                <TableCell sx={{ color: 'text.disabled' }}>{idx + 1}</TableCell>
                                <TableCell>{i.public_ip ? `${i.public_ip}:${K6_METRICS_PORT}` : '—'}</TableCell>
                                <TableCell>{i.region ?? '—'}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {i.checks?.total > 0 ? (
                                            <Chip
                                                label={`${i.checks.ok}/${i.checks.total}`}
                                                size='small'
                                                color={i.checks.ok === i.checks.total ? 'success' : 'warning'}
                                                sx={CHIP_SX}
                                            />
                                        ) : (
                                            !i.docker && (
                                                <Chip
                                                    label={i.ec2_state ?? i.status ?? '—'}
                                                    size='small'
                                                    sx={CHIP_SX}
                                                />
                                            )
                                        )}
                                        {i.docker && (
                                            <Chip
                                                label={i.docker}
                                                size='small'
                                                color={i.docker === 'up' ? 'success' : 'warning'}
                                                sx={CHIP_SX}
                                            />
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {i.metrics?.cpu_percent != null ? `${i.metrics.cpu_percent}%` : '—'}
                                </TableCell>
                                <TableCell>
                                    {i.metrics?.memory_used_pct != null ? `${i.metrics.memory_used_pct}%` : '—'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}

export { AccountMl };
